import type { WizardData } from '../../types/machine-setup-types';

const EVSE_KEYS = new Set(['medias4_single', 'medias4_duo', 'medias4_multi_residential', 'vpos_single', 'vpos_duo']);
const DUO_KEYS = new Set(['medias4_duo', 'vpos_duo']);

interface ReviewStepProps {
  wizardData: Partial<WizardData>;
  isLoading: boolean;
}

interface ReviewRow {
  label: string;
  value: string | number | null | undefined;
}

export function ReviewStep({ wizardData, isLoading }: ReviewStepProps) {
  const key = wizardData.machineTypeKey ?? '';
  const hasEvse = EVSE_KEYS.has(key);
  const isDuo = DUO_KEYS.has(key);

  const actorDisplay = wizardData.actorName
    ? `${wizardData.actorName} (ID: ${wizardData.actorId})`
    : wizardData.actorId
    ? String(wizardData.actorId)
    : '—';

  const rows: ReviewRow[] = [
    { label: 'Machine Type', value: wizardData.machineTypeName },
    { label: 'Type Key', value: wizardData.machineTypeKey },
    { label: 'Machine Name', value: wizardData.machineName },
    { label: 'Operator / Location', value: actorDisplay },
    { label: 'Serial Number', value: wizardData.serialNumber || '— (not set)' },
    ...(wizardData.machineGroupId ? [{ label: 'Machine Group', value: wizardData.machineGroupName || String(wizardData.machineGroupId) }] : []),
    ...(wizardData.tariffId ? [{ label: 'Tariff ID', value: wizardData.tariffId }] : []),
    ...(hasEvse && wizardData.maxCurrentEvse1 ? [{ label: 'EVSE 1 Max Current', value: `${wizardData.maxCurrentEvse1} A` }] : []),
    ...(isDuo && wizardData.maxCurrentEvse2 ? [{ label: 'EVSE 2 Max Current', value: `${wizardData.maxCurrentEvse2} A` }] : []),
    ...(wizardData.defaultCredit !== undefined ? [{ label: 'Default Credit', value: wizardData.defaultCredit || '— (not set)' }] : []),
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Review & Create</h2>
      <p className="text-sm text-gray-500 mb-6">
        Confirm the details below before creating the machine. Preset attributes will be applied automatically after creation.
      </p>

      <div className="max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-gray-100">
            {rows.map(({ label, value }) => (
              <tr key={label}>
                <td className="px-4 py-3 font-medium text-gray-500 w-40">{label}</td>
                <td className="px-4 py-3 text-gray-900">{String(value ?? '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isLoading && (
        <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          Creating machine in Lynx…
        </div>
      )}
    </div>
  );
}
